import React, { useMemo } from "react";
import { Box, Paper, Typography } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DirectionsBusOutlinedIcon from "@mui/icons-material/DirectionsBusOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useTranslation } from "react-i18next";
import StatsCard from "../../common/New/StatsCard";
import {
  ChunkLoadingIndicator,
  useProgressiveOverviewData,
} from "../../manageData/Analytics/useProgressiveOverviewData";

const COLORS = {
  blue: "#3578E5",
  green: "#07962F",
  amber: "#FF7600",
  red: "#F5382A",
  purple: "#9B51E0",
  cyan: "#08AFCB",
  gray: "#AAB4C3",
  text: "#172033",
  muted: "#718096",
  track: "#EDF1F6",
};

const number = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const rowsFrom = (state) => {
  const payload = state?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const unwrap = (value, keys = []) => {
  if (Array.isArray(value)) return value;
  for (const key of keys) {
    if (Array.isArray(value?.[key])) return value[key];
  }
  return [];
};

const vehicleKey = (row, index = 0) =>
  String(
    row?.deviceId ||
      row?.vehicleId ||
      row?.id ||
      row?.vehicleNumber ||
      row?.vehicleName ||
      index,
  );

const vehicleName = (row, index = 0) =>
  row?.vehicleName ||
  row?.name ||
  row?.vehicleNumber ||
  row?.registrationNumber ||
  row?.registration_number ||
  `Vehicle-${index + 1}`;

const registration = (row) =>
  row?.vehicleNumber ||
  row?.registrationNumber ||
  row?.registration_number ||
  row?.deviceName ||
  "";

const deviceHealthDescriptor = (device) =>
  [
    device?.type,
    device?.deviceType,
    device?.deviceType?.name,
    device?.deviceType?.description,
    device?.sensorType,
    device?.name,
    device?.deviceName,
  ]
    .filter((value) => typeof value === "string" && value.trim())
    .join(" ");

const formatDuration = (hours) => {
  const totalMinutes = Math.round(number(hours) * 60);
  return `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`;
};

const expectedHours = (range, customRange) => {
  if (range === "custom" && customRange?.startDate && customRange?.endDate) {
    const duration =
      (new Date(customRange.endDate).getTime() -
        new Date(customRange.startDate).getTime()) /
      3600000;
    return Math.max(duration, 1);
  }
  if (["last 7 days", "last_7_days", "this_week"].includes(range)) return 168;
  if (["last_30_days", "this_month"].includes(range)) return 720;
  if (range === "yesterday") return 24;
  const now = new Date();
  return Math.max(now.getHours() + now.getMinutes() / 60, 1);
};

function Panel({ title, action, progress, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #DDE2EA",
        borderRadius: 1.5,
        overflow: "hidden",
        position: "relative",
        minWidth: 0,
        ...sx,
      }}
    >
      <ChunkLoadingIndicator progress={progress} />
      <Box sx={{ minHeight: 54, px: 2, borderBottom: "1px solid #EEF1F5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>{title}</Typography>
        {action && <Typography sx={{ fontSize: 9, fontWeight: 700, color: COLORS.blue }}>{action}</Typography>}
      </Box>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
}

function Donut({ rows, total, centerLabel = "Total" }) {
  const { t } = useTranslation();
  const hasData = rows.some((row) => row.value > 0);
  const data = (hasData ? rows : [{ name: "No data", value: 1, color: "#E7EBF0" }])
    .map((row) => ({ ...row, name: t(row.name) }));
  return (
    <>
      <Box sx={{ height: 205, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius="62%" outerRadius="88%" stroke="none">
              {data.map((row) => <Cell key={row.name} fill={row.color} />)}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ position: "absolute", inset: 0, display: "grid", placeContent: "center", textAlign: "center", pointerEvents: "none" }}>
          <Typography sx={{ fontSize: 25, fontWeight: 800, color: COLORS.text }}>{total}</Typography>
          <Typography sx={{ fontSize: 10, color: COLORS.gray }}>{t(centerLabel)}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gap: 0.9 }}>
        {rows.map((row) => (
          <Box key={row.name} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ width: 9, height: 9, borderRadius: 0.5, bgcolor: row.color }} />
            <Typography sx={{ flex: 1, fontSize: 10, color: COLORS.text }}>{t(row.name)}</Typography>
            <Typography sx={{ fontSize: 10, color: COLORS.muted }}>
              {row.value} ({total ? ((row.value / total) * 100).toFixed(1) : "0.0"}%)
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
}

function ProgressList({ rows, valueKey, suffix = "%", color = COLORS.blue, labelKey = "name" }) {
  const { t } = useTranslation();
  const max = Math.max(...rows.map((row) => number(row[valueKey])), 1);
  return (
    <Box sx={{ display: "grid", gap: 2.2 }}>
      {rows.length ? rows.map((row) => {
        const value = number(row[valueKey]);
        const width = suffix === "%" ? Math.min(value, 100) : (value / max) * 100;
        return (
          <Box key={`${row[labelKey]}-${valueKey}`} sx={{ display: "grid", gridTemplateColumns: "54px 1fr 45px", alignItems: "center", gap: 1 }}>
            <Typography noWrap sx={{ fontSize: 9, color: COLORS.muted }}>{row[labelKey]}</Typography>
            <Box sx={{ height: 7, borderRadius: 5, bgcolor: COLORS.track, overflow: "hidden" }}>
              <Box sx={{ width: `${width}%`, height: "100%", borderRadius: 5, bgcolor: color }} />
            </Box>
            <Typography sx={{ textAlign: "right", fontSize: 9, fontWeight: 700, color: COLORS.text }}>
              {Math.round(value * 10) / 10}{suffix}
            </Typography>
          </Box>
        );
      }) : (
        <Typography sx={{ py: 7, textAlign: "center", fontSize: 11, color: COLORS.muted }}>{t("No vehicle data for the selected period")}</Typography>
      )}
    </Box>
  );
}

export default function VehicleAnalytics({
  vehiclesData = [],
  devicesData = [],
  selectedDateRange = "today",
  customDateRange = null,
}) {
  const { t } = useTranslation();
  const { chartDataStates, chunkProgress, error, isCancelling } =
    useProgressiveOverviewData(selectedDateRange, customDateRange);
  const vehicles = useMemo(() => unwrap(vehiclesData, ["data", "vehicles"]), [vehiclesData]);
  const devices = useMemo(() => unwrap(devicesData, ["data", "devices"]), [devicesData]);
  const tripState = chartDataStates.trip_vehicles;
  const runtimeState = chartDataStates.runtime_vehicles;
  const overspeedState = chartDataStates.overspeed_vehicles;
  const haltState = chartDataStates.halt_vehicles;
  const trips = useMemo(() => rowsFrom(tripState), [tripState]);
  const runtime = useMemo(() => rowsFrom(runtimeState), [runtimeState]);
  const overspeeds = useMemo(() => rowsFrom(overspeedState), [overspeedState]);
  const halts = useMemo(() => rowsFrom(haltState), [haltState]);
  const tripLoading = !error && (isCancelling || tripState?.loading || !tripState);
  const runtimeLoading = !error && (isCancelling || runtimeState?.loading || !runtimeState);
  const alertLoading = !error && (isCancelling || overspeedState?.loading || !overspeedState);
  const hoursInRange = expectedHours(selectedDateRange, customDateRange);

  const metrics = useMemo(() => {
    const completed = trips.filter((row) => String(row?.status || "").trim().toLowerCase() === "completed").length;
    const delayed = trips.filter((row) => /not completed|over/i.test(String(row?.status || ""))).length;
    const scored = completed + delayed;
    const runtimeRows = runtime.map((row, index) => {
      const moving = number(row?.totalMovingHours);
      const total = number(row?.totalRuntimeHours) || moving;
      return {
        key: vehicleKey(row, index),
        name: vehicleName(row, index),
        distance: number(row?.noOfKmsCovered),
        moving,
        idle: Math.max(
          total - moving,
          number(row?.totalIdleHours),
          number(row?.idleHours),
        ),
        utilization: Math.min(100, Math.round((moving / hoursInRange) * 100)),
      };
    });
    const distance = runtimeRows.reduce((sum, row) => sum + row.distance, 0);
    const moving = runtimeRows.reduce((sum, row) => sum + row.moving, 0);
    const idle = runtimeRows.reduce((sum, row) => sum + row.idle, 0);
    const utilization = moving + idle ? Math.round((moving / (moving + idle)) * 100) : 0;

    const alertMap = new Map();
    overspeeds.forEach((row, index) => {
      const key = vehicleKey(row, index);
      const item = alertMap.get(key) || { key, name: vehicleName(row, index), registration: registration(row), alerts: 0 };
      item.alerts += 1;
      alertMap.set(key, item);
    });
    halts.forEach((row, index) => {
      const key = vehicleKey(row, index);
      const item = alertMap.get(key) || { key, name: vehicleName(row, index), registration: registration(row), alerts: 0 };
      item.alerts += 1;
      alertMap.set(key, item);
    });
    const alertRows = Array.from(alertMap.values()).sort((a, b) => b.alerts - a.alerts);

    return {
      onTime: scored ? Math.round((completed / scored) * 100) : 0,
      distance,
      idle,
      utilization,
      runtimeRows,
      alertRows,
      vehiclesWithAlerts: alertRows.length,
      criticalAlerts: overspeeds.length,
      warningAlerts: halts.length,
    };
  }, [trips, runtime, overspeeds, halts, hoursInRange]);

  const statusRows = useMemo(() => {
    const counts = { Active: 0, Idle: 0, Alert: 0, Offline: 0 };
    const alertKeys = new Set(metrics.alertRows.map((row) => row.key));
    vehicles.forEach((vehicle, index) => {
      const status = String(vehicle?.status || vehicle?.Status || "").toUpperCase();
      if (alertKeys.has(vehicleKey(vehicle, index))) counts.Alert += 1;
      else if (["ACTIVE", "RUNNING"].includes(status)) counts.Active += 1;
      else if (["INACTIVE", "STOPPED", "IDLE"].includes(status)) counts.Idle += 1;
      else counts.Offline += 1;
    });
    return [
      { name: "Active", value: counts.Active, color: COLORS.green },
      { name: "Idle", value: counts.Idle, color: COLORS.amber },
      { name: "Alert", value: counts.Alert, color: COLORS.red },
      { name: "Offline", value: counts.Offline, color: COLORS.gray },
    ];
  }, [vehicles, metrics.alertRows]);

  const severityRows = [
    { name: "Critical", value: metrics.criticalAlerts, color: COLORS.red },
    { name: "Warning", value: metrics.warningAlerts, color: COLORS.amber },
  ];
  const alertTotal = severityRows.reduce((sum, row) => sum + row.value, 0);
  const topAlerts = metrics.alertRows.slice(0, 5);
  const utilizationRows = [...metrics.runtimeRows].sort((a, b) => b.utilization - a.utilization).slice(0, 5);
  const distanceRows = [...metrics.runtimeRows].sort((a, b) => b.distance - a.distance).slice(0, 5);
  const idleRows = [...metrics.runtimeRows].sort((a, b) => b.idle - a.idle).slice(0, 3);

  const healthRows = useMemo(() => {
    const groups = [
      { label: "Dashcam", matcher: /dash|camera/i },
      { label: "GPS Device", matcher: /gps|tracking/i },
      { label: "Fuel Sensor", matcher: /fuel/i },
    ];
    return groups.map(({ label, matcher }) => {
      const matching = devices.filter((device) =>
        matcher.test(deviceHealthDescriptor(device)),
      );
      const good = matching.filter((device) => !/dead|inactive|offline|critical/i.test(String(device?.status || ""))).length;
      return { label, total: matching.length, good, percent: matching.length ? Math.round((good / matching.length) * 1000) / 10 : 0 };
    });
  }, [devices]);

  const cards = [
    { title: "Vehicles", value: vehicles.length, icon: DirectionsBusOutlinedIcon, tone: "blue", loading: false },
    { title: "On-Time Performance", value: `${metrics.onTime}%`, icon: AccessTimeOutlinedIcon, tone: "green", loading: tripLoading },
    { title: "Avg. Utilization", value: `${metrics.utilization}%`, icon: SpeedOutlinedIcon, tone: "amber", loading: runtimeLoading },
    { title: "Distance", value: `${Math.round(metrics.distance).toLocaleString()} km`, icon: AltRouteOutlinedIcon, tone: "purple", loading: runtimeLoading },
    { title: "Idle Time", value: formatDuration(metrics.idle), icon: TimerOutlinedIcon, tone: "cyan", loading: runtimeLoading },
    { title: "Vehicles with Alerts", value: metrics.vehiclesWithAlerts, icon: ErrorOutlineOutlinedIcon, tone: "red", loading: alertLoading },
  ];

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {error && <Typography sx={{ color: COLORS.red, fontSize: 12 }}>{error}</Typography>}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(6, minmax(0, 1fr))" }, gap: 2 }}>
        {cards.map((card) => <StatsCard key={card.title} title={t(card.title)} value={card.value} icon={card.icon} tone={card.tone} loading={card.loading} />)}
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr 1fr" }, gap: 2 }}>
        <Panel title={t("Vehicles by Status")}>
          <Donut rows={statusRows} total={vehicles.length} />
        </Panel>
        <Panel title={t("Vehicles with Most Alerts")} action={t("VIEW ALL")} progress={chunkProgress.overspeed_vehicles}>
          <Box sx={{ display: "grid" }}>
            {topAlerts.length ? topAlerts.map((row) => (
              <Box key={row.key} sx={{ minHeight: 48, display: "flex", alignItems: "center", borderBottom: "1px solid #EEF1F5" }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: COLORS.text }}>{row.name}</Typography>
                  <Typography sx={{ fontSize: 9, color: COLORS.muted }}>{row.registration}</Typography>
                </Box>
                <Typography sx={{ color: COLORS.red, fontSize: 12, fontWeight: 700 }}>{row.alerts}</Typography>
              </Box>
            )) : <Typography sx={{ py: 10, textAlign: "center", fontSize: 11, color: COLORS.muted }}>{t("No vehicle alerts")}</Typography>}
          </Box>
        </Panel>
        <Panel title={t("Alerts by Severity")} progress={chunkProgress.overspeed_vehicles}>
          <Donut rows={severityRows} total={alertTotal} />
        </Panel>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }, gap: 2 }}>
        <Panel title={t("Utilization by Vehicle (%)")} action={t("VIEW ALL")} progress={chunkProgress.runtime_vehicles}>
          <ProgressList rows={utilizationRows} valueKey="utilization" />
        </Panel>
        <Panel title={t("Distance Travelled (km)")} action={t("VIEW ALL")} progress={chunkProgress.runtime_vehicles}>
          <ProgressList rows={distanceRows} valueKey="distance" suffix="" />
        </Panel>
        <Panel title={t("Idle Time by Vehicle (hrs)")} action={t("VIEW ALL")} progress={chunkProgress.runtime_vehicles}>
          <ProgressList rows={idleRows} valueKey="idle" suffix="h" color={COLORS.red} />
        </Panel>
        <Panel title={t("Fleet Health Summary")} action={t("VIEW ALL")}>
          <Box sx={{ display: "grid", gap: 2.4, pt: 1 }}>
            {healthRows.map((row) => (
              <Box key={row.label} sx={{ display: "grid", gridTemplateColumns: "74px 1fr 58px", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 9, color: COLORS.muted }}>{t(row.label)}</Typography>
                <Box sx={{ height: 7, bgcolor: COLORS.track, borderRadius: 5, overflow: "hidden" }}>
                  <Box sx={{ width: `${row.percent}%`, height: "100%", bgcolor: COLORS.green, borderRadius: 5 }} />
                </Box>
                <Typography sx={{ fontSize: 9, color: COLORS.text }}>{row.good} ({row.percent}%)</Typography>
              </Box>
            ))}
            {!devices.length && <Typography sx={{ textAlign: "center", fontSize: 10, color: COLORS.muted }}>{t("No device health data available")}</Typography>}
            <Box sx={{ borderTop: "1px solid #EEF1F5", pt: 2, display: "flex", justifyContent: "center", gap: 2 }}>
              {[["GOOD", COLORS.green], ["WARNING", COLORS.amber], ["CRITICAL", COLORS.red]].map(([label, color]) => (
                <Typography key={label} sx={{ fontSize: 8, color: COLORS.muted }}><Box component="span" sx={{ display: "inline-block", width: 7, height: 7, mr: 0.5, borderRadius: 0.4, bgcolor: color }} />{t(label)}</Typography>
              ))}
            </Box>
          </Box>
        </Panel>
      </Box>
    </Box>
  );
}
